import { useState, useCallback, useRef } from 'react';

import { Staff } from '../../../types';
import { getStaffList } from '../../../api';

const PAGE_SIZE = 25;

type UseStaffListReturn = {
  staffList: Staff[];
  isLoading: boolean;
  hasMore: boolean;
  searchQuery: string;
  search: (query: string) => Promise<Staff[]>;
  loadMore: () => Promise<void>;
  loadInitial: () => Promise<Staff[]>;
};

/**
 * Hook for managing staff list with pagination and search.
 * Handles race conditions using a version counter pattern.
 */
export const useStaffList = (): UseStaffListReturn => {
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Version counter to prevent race conditions
  const requestVersionRef = useRef(0);

  const loadInitial = useCallback(async(): Promise<Staff[]> => {
    const requestVersion = ++requestVersionRef.current;
    setIsLoading(true);
    setCurrentPage(1);

    try {
      const response = await getStaffList({ page: 1, page_size: PAGE_SIZE });

      if (requestVersion !== requestVersionRef.current) {
        return [];
      }

      setStaffList(response.results);
      setHasMore(response.next !== null);
      return response.results;
    } catch (error) {
      if (requestVersion === requestVersionRef.current) {
        console.error('Failed to load staff list:', error);
        setStaffList([]);
        setHasMore(false);
      }
      return [];
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const search = useCallback(async(query: string): Promise<Staff[]> => {
    const requestVersion = ++requestVersionRef.current;
    setIsLoading(true);
    setCurrentPage(1);

    try {
      const response = await getStaffList({
        search: query || undefined,
        page: 1,
        page_size: PAGE_SIZE,
      });

      if (requestVersion !== requestVersionRef.current) {
        return [];
      }

      setSearchQuery(query);
      setStaffList(response.results);
      setHasMore(response.next !== null);
      return response.results;
    } catch (error) {
      if (requestVersion === requestVersionRef.current) {
        console.error('Failed to search staff:', error);
        setStaffList([]);
        setHasMore(false);
      }
      return [];
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const loadMore = useCallback(async(): Promise<void> => {
    if (isLoading || !hasMore) {
      return;
    }

    const requestVersion = requestVersionRef.current;
    setIsLoading(true);

    try {
      const nextPage = currentPage + 1;
      const response = await getStaffList({
        search: searchQuery || undefined,
        page: nextPage,
        page_size: PAGE_SIZE,
      });

      if (requestVersion !== requestVersionRef.current) {
        return;
      }

      setStaffList((prev) => [...prev, ...response.results]);
      setCurrentPage(nextPage);
      setHasMore(response.next !== null);
    } catch (error) {
      if (requestVersion === requestVersionRef.current) {
        console.error('Failed to load more staff:', error);
      }
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setIsLoading(false);
      }
    }
  }, [currentPage, hasMore, isLoading, searchQuery]);

  return {
    staffList,
    isLoading,
    hasMore,
    searchQuery,
    search,
    loadMore,
    loadInitial,
  };
};
